import React, {useState} from 'react';

import {Sidebar} from "primereact/sidebar";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {Tag} from "primereact/tag";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {ExportAsyncService} from "../../service/ExportAsyncService";
import {exportFile} from "../../utils/Utils";
import {toDateTimeString, toHumanizedDuration, toRelativeTimeString} from "../../utils/TimeUtil";
import {inProgressCount, normalizeReportState} from "./reportState";

const STATE_SEVERITY = {done: 'success', failed: 'danger', in_progress: 'info'};
const STATE_ICON = {done: 'pi pi-check', failed: 'pi pi-times-circle', in_progress: 'pi pi-spin pi-spinner'};

const DownloadReportPanel = (props) => {

    const {visible, reports, onHide, onRefresh, lastUpdatedAt} = props;

    const {t, toast, setError} = React.useContext(AppContext);
    const {campaigns} = React.useContext(CampaignContext);

    const [downloadingId, setDownloadingId] = useState(null);
    const [retryingId, setRetryingId] = useState(null);

    const exportAsyncService = new ExportAsyncService();

    // A report that failed before it produced a file has no file name, so the
    // process type is what identifies it. Older records predate the field and
    // fall back to the file name.
    const reportTitle = (rowData) => {
        if (rowData.process_type)
            return t('report.type.' + rowData.process_type, {defaultValue: rowData.process_type.replace(/_/g, ' ')});
        return rowData.report_key || t('report.unnamed');
    };

    const campaignName = (rowData) => {
        const campaignId = rowData.param && rowData.param.campaign_id;
        const campaign = campaignId && (campaigns || []).find(c => c.id === campaignId);
        return campaign ? campaign.name : null;
    };

    const meta = (rowData) => {
        const parts = [];
        const campaign = campaignName(rowData);
        if (campaign)
            parts.push(campaign);
        if (rowData.created_at)
            parts.push(toRelativeTimeString(rowData.created_at));
        if (rowData.state === 'done' && rowData.created_at && rowData.finished_at) {
            const elapsed = new Date(rowData.finished_at) - new Date(rowData.created_at);
            if (elapsed > 0)
                parts.push(t('report.duration', {duration: toHumanizedDuration(elapsed)}));
        }
        return parts.join(' · ');
    };

    const reportTemplate = (rowData) => {
        const state = normalizeReportState(rowData.state);
        const isNew = state === 'done' && rowData.is_downloaded === false;
        return <div title={rowData.report_key || ''}>
            <div style={{fontWeight: 500}}>
                {reportTitle(rowData)}
                {isNew && <Tag value={t('report.new')} severity="success" style={{marginLeft: '0.5em', fontSize: '0.7rem'}}/>}
            </div>
            <div style={{fontSize: '0.8rem', color: 'var(--text-color-secondary)'}}
                 title={toDateTimeString(rowData.created_at)}>
                {meta(rowData)}
            </div>
            {state === 'failed' && rowData.exception_message &&
                <div style={{fontSize: '0.8rem', marginTop: '0.4em'}} className="p-error">
                    <i className="pi pi-exclamation-triangle" style={{marginRight: '0.3em'}}/>
                    {rowData.exception_message}
                </div>}
        </div>;
    };

    const stateTemplate = (rowData) => {
        const state = normalizeReportState(rowData.state);
        return <Tag value={t('report.state.' + state)} severity={STATE_SEVERITY[state]} icon={STATE_ICON[state]}/>;
    };

    const downloadReport = (rowData) => {
        setDownloadingId(rowData.id);
        exportAsyncService.downloadGeneratedReport(rowData.report_key).then(response => {
            exportFile(response, rowData.report_key);
            setDownloadingId(null);
            onRefresh();
        }).catch(error => {
            setError(error);
            setError(null);
            setDownloadingId(null);
        });
    };

    const retryReport = (rowData) => {
        setRetryingId(rowData.id);
        exportAsyncService.retryGeneratedReport(rowData.process_type, rowData.param).then(() => {
            setRetryingId(null);
            toast.current.show({severity: 'success', summary: t('report.retry_queued')});
            onRefresh();
        }).catch(error => {
            setError(error);
            setError(null);
            setRetryingId(null);
        });
    };

    const actionTemplate = (rowData) => {
        const state = normalizeReportState(rowData.state);
        if (state === 'done')
            return <Button label={t('report.action')} icon="pi pi-file-excel" className="p-button-sm p-button-success"
                           loading={downloadingId === rowData.id} onClick={() => downloadReport(rowData)}/>;
        if (state === 'failed' && rowData.process_type)
            return <Button label={t('report.retry')} icon="pi pi-replay" className="p-button-sm p-button-outlined"
                           loading={retryingId === rowData.id} onClick={() => retryReport(rowData)}/>;
        return null;
    };

    const generatingCount = inProgressCount(reports);

    const header = <div className="p-d-flex p-ai-center" style={{gap: '0.75em'}}>
        <i className="pi pi-cloud-download" style={{fontSize: '1.25rem'}}/>
        <div style={{flex: 1}}>
            <div>{t('report.my_reports')}</div>
            <div style={{fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-color-secondary)'}}>
                {generatingCount > 0 ? t('report.in_progress_count', {count: generatingCount}) + ' · ' : ''}
                {lastUpdatedAt ? t('report.last_updated', {time: toRelativeTimeString(lastUpdatedAt)}) : ''}
            </div>
        </div>
        <Button icon="pi pi-refresh" className="p-button-text p-button-rounded" onClick={onRefresh}
                tooltip={t('report.refresh')} tooltipOptions={{position: 'bottom'}}/>
    </div>;

    const emptyMessage = <div style={{textAlign: 'center', padding: '2em 0'}}>
        <i className="pi pi-inbox" style={{fontSize: '2rem', color: 'var(--text-color-secondary)'}}/>
        <div style={{marginTop: '0.5em', fontWeight: 500}}>{t('report.empty_title')}</div>
        <div style={{color: 'var(--text-color-secondary)'}}>{t('report.empty_desc')}</div>
    </div>;

    const rowClassName = (rowData) => ({
        'download-report-row-new': normalizeReportState(rowData.state) === 'done' && rowData.is_downloaded === false
    });

    return <Sidebar visible={visible} position="right" onHide={onHide}
                    style={{width: '42em', maxWidth: '96vw'}}>
        <DataTable value={reports} className="p-datatable-sm" header={header} responsive={true}
                   sortField="created_at" sortOrder={-1} rowClassName={rowClassName}
                   paginator={(reports || []).length > 10} rows={10} emptyMessage={emptyMessage}>
            <Column body={reportTemplate} header={t('report.name')}/>
            <Column body={stateTemplate} header={t('common.state')} style={{width: '9em'}}/>
            <Column body={actionTemplate} header={t('report.actions')} style={{width: '11em', textAlign: 'right'}}/>
        </DataTable>
    </Sidebar>;
};

export default DownloadReportPanel;
